/**
 * PublicShareGate
 *
 * The three not-ready states shared by the public shared-map pages: loading
 * spinner, fatal status (notFound / revoked / error), and the password gate.
 * Pages render it whenever the share is not fully ready (config + data loaded)
 * and `return` it before their own layout:
 *
 * ```tsx
 * if (status !== 'ready' || !config || !data) {
 *   return <PublicShareGate status={status} ... />;
 * }
 * ```
 *
 * The password gate is also the catch-all for any non-loading / non-fatal
 * not-ready state (e.g. config/data still missing), mirroring the original
 * inline `status === 'password' || !config || !data` branch.
 *
 * @module components/share/public/PublicShareGate
 */
import type { ReactElement } from 'react';

import { PublicPasswordGate } from './PublicPasswordGate';
import { PublicShareStatus } from './PublicShareStatus';
import type { PublicShareStatus as PublicShareStatusValue } from './usePublicShare';

interface PublicShareGateProps {
  status: PublicShareStatusValue;
  passwordError: boolean;
  rateLimited: boolean;
  onSubmitPassword: (password: string) => void;
}

export function PublicShareGate({
  status,
  passwordError,
  rateLimited,
  onSubmitPassword,
}: PublicShareGateProps): ReactElement {
  if (status === 'loading') {
    return <PublicShareStatus variant="loading" />;
  }
  if (status === 'notFound' || status === 'revoked' || status === 'error') {
    return <PublicShareStatus variant={status} />;
  }
  return (
    <PublicPasswordGate
      hasError={passwordError}
      isRateLimited={rateLimited}
      onSubmit={onSubmitPassword}
    />
  );
}
